/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/ScrcpyMirror.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-15 (Step 4)  | Claude Sonnet 4.6 | scrcpy mirror (Step 4) | Created from scratch |
 * | 2 | 2026-06-16 (bugfix)  | Claude Sonnet 4.6 | black canvas on Chromium | Replace BitmapVideoFrameRenderer (bitmaprenderer ctx goes silent after canvas.width reset) with inline 2D-context renderer; add device hygiene commands |
 * | 3 | 2026-06-16 (bugfix3) | Claude Sonnet 4.6 | verification indicator   | Add framesRendered to MirrorState.streaming; renderer increments and fires onFrameDrawn callback, throttled to avoid re-render flood |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx             (ScrcpyMirror class, MirrorState type)
 * - apps/studio/src/device/ScrcpyMirror.test.ts  (unit tests via injected deps)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - MirrorState       (discriminated union — all possible mirror states)
 * - ScrcpyMirrorDeps  (injectable deps for testing)
 * - ScrcpyMirror      (class: start(), stop(), subscribe(), get state())
 *
 * CALLER → CALLEE MAP
 * --------------------
 * App.tsx → new ScrcpyMirror() → mirror.start(adb, canvas)
 *                                        ↓
 *   deps.fetchBin()                  (fetch BIN from @yume-chan/fetch-scrcpy-server)
 *         ↓
 *   deps.push(adb, stream)           (AdbScrcpyClient.pushServer)
 *         ↓
 *   deps.startClient(adb)            (AdbScrcpyClient.start with AdbScrcpyOptionsLatest)
 *         ↓
 *   deps.createDecoder(codec, canvas) (Promise<decoder>: WebCodecsVideoDecoder + BitmapVideoFrameRenderer)
 *         ↓
 *   videoStream.stream.pipeTo(decoder.writable)  (runs until stop() or device disconnect)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. State machine: idle → pushing → starting → streaming(w,h) → stopped | error
 *    Any state → error (on exception); streaming → stopped (on stop() call).
 * 2. All four I/O operations are injectable. Tests inject synchronous fakes;
 *    browser uses dynamic-import defaults that load browser-only modules lazily.
 * 3. `createDecoder` returns a Promise so the WebCodecs module can be lazily
 *    imported. Never reaches the import call in Node.js tests (injected).
 * 4. audio: false is intentional — Phase 0 does not need audio; not consuming
 *    the audio stream would block the ADB connection.
 * 5. AdbScrcpyClient.start() auto-falls-back reverse→forward tunnel on
 *    AdbReverseNotSupportedError — we don't handle that ourselves.
 * 6. pipeTo() runs in the background. stop() calls client.close() which ends
 *    the ReadableStream, completing the pipe and triggering decoder.dispose().
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - BitmapVideoFrameRenderer and WebCodecsVideoDecoder are browser-only.
 *   They are loaded via dynamic import() so Node.js tests skip them (injection).
 * - MaybeConsumable<Uint8Array> is a supertype of Uint8Array; a plain
 *   ReadableStream<Uint8Array> from fetch().body is cast-compatible.
 * - ScrcpyVideoCodecId values are numeric constants, not an enum.
 *
 * =============================================================================
 */

import type { Adb } from "@yume-chan/adb";
import type { ScrcpyMediaStreamPacket } from "@yume-chan/scrcpy";
import { ScrcpyVideoCodecId } from "@yume-chan/scrcpy";
import type { ReadableStream, WritableStream } from "@yume-chan/stream-extra";

/* ── Internal handle interfaces (structural — kept minimal for testability) ── */

export interface MirrorVideoStreamHandle {
  codec: ScrcpyVideoCodecId;
  stream: ReadableStream<ScrcpyMediaStreamPacket>;
}

export interface MirrorClientHandle {
  getVideoStream(): Promise<MirrorVideoStreamHandle>;
  exited: Promise<void>;
  close(): Promise<void>;
}

export interface MirrorDecoderHandle {
  writable: WritableStream<ScrcpyMediaStreamPacket>;
  onSizeChange(listener: (width: number, height: number) => void): void;
  onFrameDrawn(listener: (count: number) => void): void;
  dispose(): void;
}

/* ── State ────────────────────────────────────────────────────────────────── */

export type MirrorState =
  | { status: "idle" }
  | { status: "pushing" }
  | { status: "starting" }
  | { status: "streaming"; width: number; height: number; framesRendered: number }
  | { status: "error"; message: string }
  | { status: "stopped" };

export type MirrorStateListener = (state: MirrorState) => void;

/* ── Injectable dependencies ──────────────────────────────────────────────── */

export interface ScrcpyMirrorDeps {
  /** Fetches the scrcpy server binary. Default: fetch(BIN) from fetch-scrcpy-server. */
  fetchBin?: () => Promise<ReadableStream<Uint8Array>>;

  /** Pushes binary to device via ADB sync. Default: AdbScrcpyClient.pushServer. */
  push?: (adb: Adb, stream: ReadableStream<Uint8Array>) => Promise<void>;

  /** Starts the scrcpy process. Default: AdbScrcpyClient.start (video=true, audio=false). */
  startClient?: (adb: Adb) => Promise<MirrorClientHandle>;

  /** Creates a video decoder (async so WebCodecs module can be lazy-imported).
   *  Default: WebCodecsVideoDecoder + BitmapVideoFrameRenderer. */
  createDecoder?: (codec: ScrcpyVideoCodecId, canvas: HTMLCanvasElement) => Promise<MirrorDecoderHandle>;
}

/* ── Default implementations ──────────────────────────────────────────────── */

async function defaultFetchBin(): Promise<ReadableStream<Uint8Array>> {
  const { BIN } = await import("@yume-chan/fetch-scrcpy-server");
  const res = await fetch(BIN);
  if (!res.ok || !res.body) {
    throw new Error(`Failed to fetch scrcpy server binary: ${res.status}`);
  }
  return res.body as unknown as ReadableStream<Uint8Array>;
}

async function defaultPush(adb: Adb, stream: ReadableStream<Uint8Array>): Promise<void> {
  const { AdbScrcpyClient } = await import("@yume-chan/adb-scrcpy");
  await AdbScrcpyClient.pushServer(
    adb,
    stream as unknown as import("@yume-chan/stream-extra").ReadableStream<
      import("@yume-chan/stream-extra").MaybeConsumable<Uint8Array>
    >,
  );
}

async function defaultStartClient(adb: Adb): Promise<MirrorClientHandle> {
  const { AdbScrcpyClient, AdbScrcpyOptionsLatest } = await import("@yume-chan/adb-scrcpy");
  const { DefaultServerPath } = await import("@yume-chan/scrcpy");

  const options = new AdbScrcpyOptionsLatest<true>({
    video: true,
    audio: false,
    control: true,
    maxSize: 0,
    videoBitRate: 4_000_000,
  });

  const client = await AdbScrcpyClient.start(adb, DefaultServerPath, options);

  return {
    async getVideoStream() {
      const vs = await (client.videoStream as Promise<{
        metadata: { codec: ScrcpyVideoCodecId };
        stream: ReadableStream<ScrcpyMediaStreamPacket>;
      }>);
      return {
        codec: vs.metadata.codec,
        stream: vs.stream,
      };
    },
    exited: client.exited,
    close: () => client.close(),
  };
}

async function defaultCreateDecoder(
  codec: ScrcpyVideoCodecId,
  canvas: HTMLCanvasElement,
): Promise<MirrorDecoderHandle> {
  const { WebCodecsVideoDecoder } =
    await import("@yume-chan/scrcpy-decoder-webcodecs");

  // BitmapVideoFrameRenderer (ImageBitmapRenderingContext) goes silent after
  // canvas.width is reassigned on first frame — a Chromium quirk where the
  // bitmaprenderer context stops updating the visible canvas post-resize.
  // The 2d context is reset on resize (cleared to black) but the object stays
  // valid and drawImage works immediately. Re-acquiring it after setSize() is
  // the safest pattern.
  let ctx: CanvasRenderingContext2D | null = canvas.getContext("2d");
  let frameCount = 0;
  let frameListener: ((count: number) => void) | null = null;

  const renderer = {
    setSize(w: number, h: number) {
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        ctx = canvas.getContext("2d");
      }
    },
    draw(frame: VideoFrame) {
      if (!ctx) return;
      ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
      frameCount++;
      // Fire on the 1st frame (proves rendering works) then every 30th frame
      // (~1 s at 30 fps) so the UI shows a live counter without flooding React.
      if (frameCount === 1 || frameCount % 30 === 0) {
        frameListener?.(frameCount);
      }
    },
  };

  const decoder = new WebCodecsVideoDecoder({ codec, renderer });

  return {
    writable: decoder.writable as unknown as WritableStream<ScrcpyMediaStreamPacket>,
    onSizeChange(listener) {
      decoder.sizeChanged((size) => listener(size.width, size.height));
    },
    onFrameDrawn(listener) {
      frameListener = listener;
    },
    dispose() {
      frameListener = null;
      decoder.dispose();
    },
  };
}

/* ── ScrcpyMirror ─────────────────────────────────────────────────────────── */

export class ScrcpyMirror {
  #state: MirrorState = { status: "idle" };
  #listeners = new Set<MirrorStateListener>();
  #client: MirrorClientHandle | null = null;
  #decoder: MirrorDecoderHandle | null = null;

  readonly #fetchBin: () => Promise<ReadableStream<Uint8Array>>;
  readonly #push: (adb: Adb, stream: ReadableStream<Uint8Array>) => Promise<void>;
  readonly #startClient: (adb: Adb) => Promise<MirrorClientHandle>;
  readonly #createDecoder: (codec: ScrcpyVideoCodecId, canvas: HTMLCanvasElement) => Promise<MirrorDecoderHandle>;

  constructor(deps: ScrcpyMirrorDeps = {}) {
    this.#fetchBin = deps.fetchBin ?? defaultFetchBin;
    this.#push = deps.push ?? defaultPush;
    this.#startClient = deps.startClient ?? defaultStartClient;
    this.#createDecoder = deps.createDecoder ?? defaultCreateDecoder;
  }

  /* ── State ──────────────────────────────────────────────────────────────── */

  get state(): MirrorState {
    return this.#state;
  }

  subscribe(listener: MirrorStateListener): () => void {
    this.#listeners.add(listener);
    listener(this.#state);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #setState(next: MirrorState): void {
    this.#state = next;
    for (const l of this.#listeners) l(next);
  }

  /* ── start() ────────────────────────────────────────────────────────────── */

  async start(adb: Adb, canvas: HTMLCanvasElement): Promise<void> {
    const { status } = this.#state;
    if (status === "pushing" || status === "starting" || status === "streaming") {
      return;
    }

    try {
      // ── 0: Device hygiene (non-critical; swallowed on OEM restriction) ─
      this.#setState({ status: "pushing" });
      try {
        const shell = adb.subprocess.noneProtocol;
        await shell.spawnWaitText(["settings", "put", "global", "window_animation_scale", "0"]);
        await shell.spawnWaitText(["settings", "put", "global", "transition_animation_scale", "0"]);
        await shell.spawnWaitText(["settings", "put", "global", "animator_duration_scale", "0"]);
        await shell.spawnWaitText(["svc", "power", "stayon", "usb"]);
        await shell.spawnWaitText(["input", "keyevent", "KEYCODE_WAKEUP"]);
      } catch {
        // Some OEMs restrict settings/svc without root; not fatal
      }

      // ── 1: Fetch + push server binary ──────────────────────────────────
      const bin = await this.#fetchBin();
      await this.#push(adb, bin);

      // ── 2: Start scrcpy process ────────────────────────────────────────
      this.#setState({ status: "starting" });
      const client = await this.#startClient(adb);
      this.#client = client;

      // ── 3: Resolve video stream ────────────────────────────────────────
      const videoStream = await client.getVideoStream();

      // ── 4: Create decoder ─────────────────────────────────────────────
      const decoder = await this.#createDecoder(videoStream.codec, canvas);
      this.#decoder = decoder;

      let streamWidth = 0;
      let streamHeight = 0;
      let streamFrames = 0;
      decoder.onSizeChange((w, h) => {
        streamWidth = w;
        streamHeight = h;
        if (this.#state.status === "streaming") {
          this.#setState({ status: "streaming", width: w, height: h, framesRendered: streamFrames });
        }
      });
      decoder.onFrameDrawn((count) => {
        streamFrames = count;
        if (this.#state.status === "streaming") {
          this.#setState({
            status: "streaming",
            width: streamWidth,
            height: streamHeight,
            framesRendered: count,
          });
        }
      });

      this.#setState({ status: "streaming", width: streamWidth, height: streamHeight, framesRendered: 0 });

      // ── 5: Pipe video → decoder (background) ──────────────────────────
      videoStream.stream
        .pipeTo(decoder.writable)
        .catch(() => {})
        .finally(() => {
          decoder.dispose();
          this.#decoder = null;
          if (this.#state.status === "streaming") {
            this.#setState({ status: "stopped" });
          }
        });

      client.exited.catch(() => {}).finally(() => {
        if (this.#state.status === "streaming") {
          this.#setState({ status: "stopped" });
        }
      });
    } catch (err) {
      this.#decoder?.dispose();
      this.#decoder = null;
      this.#client = null;
      const message = err instanceof Error ? err.message : String(err);
      this.#setState({ status: "error", message });
    }
  }

  /* ── stop() ─────────────────────────────────────────────────────────────── */

  async stop(): Promise<void> {
    const client = this.#client;
    this.#client = null;
    if (client) {
      await client.close().catch(() => {});
    }
    const { status } = this.#state;
    if (status !== "idle" && status !== "stopped") {
      this.#setState({ status: "stopped" });
    }
  }
}
